import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import type { Subscription } from 'rxjs';
import { interval } from 'rxjs';

import { StringConstants } from 'app/rcr-exchange/constants/string-constants';
import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-upload-pool-confirmation-page',
  templateUrl: './upload-pool-confirmation-page.component.html',
  styleUrls: ['./upload-pool-confirmation-page.component.scss'],
})
export class UploadPoolConfirmationPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public commitRequest: any;
  public commitResponse: any;
  public isAllowToNavigateAway = true;
  public allocatedCount = 0;
  public rejectedCount = 0;
  public errorCount = 0;
  navSubscription: Subscription;
  public displayNavigateAwayPopup: boolean;
  public destinationUrl = '';
  public errorReportResponse: any;
  public isErrorReportDownloaded: boolean;
  public downloadReportSubscription: Subscription;
  public timer: any;
  public confirmationStatusSubscription: Subscription;
  public displayStatusPollingPopup = false;
  public commituuid: any;
  public showUnforeseenLegend = false;
  public downloadErrorLinkClicked = true;
  public disclaimerMessage = '';

  private passDataService = inject(PassDataService);
  private megaService = inject(MegasService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.commitRequest = this.passDataService && this.passDataService.getMultiMegaCommitRequest();
    this.displayStatusPollingPopup = true;
    this.downloadErrorLinkClicked = true;
    this.timer = interval(7000);
    this.commituuid = this.passDataService.getCommitUploadCollateralResponse();
    this.confirmationStatusSubscription = this.timer.subscribe(() => {
      this.displayStatusPollingPopup = true;
      this.getConfirmationStatus();
    });
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  ngOnDestroy() {
    this.displayStatusPollingPopup = false;
    this.passDataService.setMultiMegaCommitRequest(null);
    if (this.confirmationStatusSubscription) {
      this.confirmationStatusSubscription.unsubscribe();
    }
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public navigatePageTo(pageURL) {
    this.passDataService.setEditMegaFlow(false);
    this.router.navigate([pageURL]);
  }

  public navigationController() {
    this.navSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('allocate-pools') > 0) {
          this.isAllowToNavigateAway = false;
        } else if (event.url.indexOf('confirmation') > 0) {
          this.isAllowToNavigateAway = true;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('validate-pools') > 0) {
          this.isAllowToNavigateAway = false;
          this.displayNavigateAwayPopup = true;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('upload-pool-info') > 0) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('error') > 0) {
          this.isAllowToNavigateAway = true;
        } else {
          this.isAllowToNavigateAway = true;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public scrollToReject(): void {
    let scrollToReject = 0;
    scrollToReject = 450 + this.allocatedCount * 40;
    window.scrollTo(0, scrollToReject);
  }

  public scrollToError(): void {
    let scrollToError = 0;
    scrollToError = 600 + this.allocatedCount * 40 + this.rejectedCount * 20;
    window.scrollTo(0, scrollToError);
  }

  public downloadFullErrorReport(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    if (this.downloadErrorLinkClicked) {
      this.downloadReportSubscription = this.megaService
        .downloadErrorReport(request, { observe: 'response' })
        .subscribe({
          next: (data) => this.handledownloadErrorReportSuccess(data),
          error: (error) => this.handleDownloadErrorReportFail(error),
        });
    }

    this.downloadErrorLinkClicked = false;
  }

  public handledownloadErrorReportSuccess(errorReportResponse): void {
    this.isErrorReportDownloaded = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.errorReportResponse = errorReportResponse;
    let data = this.errorReportResponse.body;
    const contentDisposition = errorReportResponse.headers.get('content-disposition');
    const fileName =
      contentDisposition &&
      contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim().replace(/"/g, '');
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.downloadErrorLinkClicked = true;
    this.cdr.markForCheck();
  }

  public handleDownloadErrorReportFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (this.downloadReportSubscription) {
      this.downloadReportSubscription.unsubscribe();
    }
    this.downloadErrorLinkClicked = true;
    this.cdr.markForCheck();
  }

  public getConfirmationStatus(): void {
    this.displayStatusPollingPopup = true;
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();

    this.megaService.confirmationStatus(request).subscribe({
      next: (data) => this.handleConfirmationStatusSuccess(data),
      error: (error) => this.handleConfirmationStatusMegaServiceFail(error),
    });
  }

  public handleConfirmationStatusSuccess(response) {
    if (response && response.jobStatus !== 'Validation Completed') {
      this.commitResponse = response && response.commitAllocationResponse;
      this.displayStatusPollingPopup = false;
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.commitResponse.failedMegas.forEach((item) => {
        if (item.errorType === 'System Error') {
          this.showUnforeseenLegend = true;
        }
      });
      if (this.confirmationStatusSubscription) {
        this.confirmationStatusSubscription.unsubscribe();
      }
      this.allocatedCount =
        this.commitResponse.allocatedMegas && this.commitResponse.allocatedMegas.length;
      this.rejectedCount =
        this.commitResponse.rejectedMegas && this.commitResponse.rejectedMegas.length;
      this.errorCount = this.commitResponse.failedMegas && this.commitResponse.failedMegas.length;
      this.cdr.markForCheck();
    }
  }

  public handleConfirmationStatusMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.displayStatusPollingPopup = false;
    if (this.confirmationStatusSubscription) {
      this.confirmationStatusSubscription.unsubscribe();
    }
    this.cdr.markForCheck();
  }

  public needsDisclaimer(allocatedMega: any): boolean {
    const matchingMega = this.commitRequest.megaDetails.find(
      (mega) => mega.megaNumber === allocatedMega.megaNumber,
    );
    if (
      matchingMega &&
      this.feeDisclaimerService.needsDisclaimer(allocatedMega.megaPrefix) &&
      matchingMega.hasFreddieCollateral
    ) {
      this.disclaimerMessage = StringConstants.freddieComputedFeeDisclaimer(
        this.passDataService.getFeeTickValue(),
      );
      return true;
    }
    return false;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
